// built-from: /engagement-repo-connection v1 impl:engagement-repo-connection role:implementation
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Github, ShieldCheck, CheckCircle2, ArrowLeft } from 'lucide-react';
import { useKnowledgeRepoConnect } from '../hooks/useKnowledgeRepoConnect';
import '../styles/KnowledgeRepoView.css';

// 072-engagement-repo-connection — the /knowledge-repo engagement-level connect
// setting, reworked from 035's zero-argument single button to a repo URL +
// fine-grained access token the consultant supplies once per engagement. It
// links each engagement to its OWN GitHub repository (no shared vendor org):
// pasting a URL to an existing writable repo links it; pasting one for a repo
// that does not exist yet creates it (private). The token is write-only — it
// is entered, POSTed once, and never shown again or read back from the server.
//
// The surface reaches the API ONLY through useKnowledgeRepoConnect
// (constitution III: never `fetch` inside a component). It renders the states
// the hook publishes: idle/error form (URL + token + Connect), connecting
// (disabled, in-place text), and connected (owner/repo + Change + Disconnect).

// Each typed failure code maps to a plain-language FIX the alert states —
// never a raw code.
const ERROR_FIX: Record<string, string> = {
  TOKEN_REQUIRED: 'Enter an access token with write access to this repository.',
  INVALID_URL: 'Enter a valid GitHub repository URL, like https://github.com/owner/repo.',
  NOT_CONFIGURED:
    "GitHub isn't configured for this workspace yet. Contact your administrator, then try again.",
  NO_PERMISSION:
    'The token does not have write access to this repository. Grant write permission, then retry.',
  REPO_CREATE_FAILED:
    'The repository could not be created. Try again, or contact your administrator.',
};

function fixFor(code: string | undefined): string {
  if (code && ERROR_FIX[code]) return ERROR_FIX[code];
  return 'The repository could not be linked. Try again, or contact your administrator.';
}

const KnowledgeRepoView: React.FC = () => {
  const navigate = useNavigate();
  const { status, error, linked, connect } = useKnowledgeRepoConnect();

  const [url, setUrl] = useState('');
  const [token, setToken] = useState('');
  // A local override so "Change" can bring the form back up even once linked
  // — the hook itself has no reset. Cleared automatically on a fresh link.
  const [editing, setEditing] = useState(false);

  const headingRef = useRef<HTMLHeadingElement | null>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  useEffect(() => {
    if (status === 'linked') setEditing(false);
  }, [status]);

  const goBack = useCallback(() => {
    navigate('/');
  }, [navigate]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') goBack();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [goBack]);

  const connecting = status === 'connecting';

  const onConnect = useCallback(() => {
    if (connecting) return; // no double-submit
    void connect({ url, token });
  }, [connecting, connect, url, token]);

  const showConnected = !editing && status === 'linked' && !!linked;

  // ── Connected ────────────────────────────────────────────────────────────
  if (showConnected && linked) {
    return (
      <div className="krv-root" data-testid="knowledge-repo-view">
        <header className="krv-header">
          <button
            type="button"
            className="krv-back"
            aria-label="Back to setup"
            onClick={goBack}
          >
            <ArrowLeft size={18} aria-hidden="true" />
          </button>
          <h1 ref={headingRef} tabIndex={-1} className="krv-heading">
            Knowledge repository
          </h1>
        </header>
        <main className="krv-body">
          <div className="krv-linked-strip">
            <CheckCircle2 size={20} aria-hidden="true" className="krv-linked-icon" />
            <span className="krv-linked-text">
              Connected: {linked.owner}/{linked.repo}
            </span>
            <button type="button" className="krv-secondary" onClick={() => setEditing(true)}>
              Change
            </button>
            <button type="button" className="krv-secondary">
              Disconnect
            </button>
          </div>
          <p className="krv-vendor-note">
            Completed activities publish here automatically. The access token is
            stored encrypted and never shown again.
          </p>
        </main>
      </div>
    );
  }

  // ── Not connected: URL + token form (idle / connecting / error) ─────────
  return (
    <div className="krv-root" data-testid="knowledge-repo-view">
      <header className="krv-header">
        <button
          type="button"
          className="krv-back"
          aria-label="Back to setup"
          onClick={goBack}
        >
          <ArrowLeft size={18} aria-hidden="true" />
        </button>
        <h1 ref={headingRef} tabIndex={-1} className="krv-heading">
          Knowledge repository
        </h1>
      </header>

      <main className="krv-body">
        <p className="krv-lead">
          Each engagement has its own GitHub repository. Paste the repo URL and
          a fine-grained access token with write access to it — if the repo
          doesn&apos;t exist yet, we&apos;ll create it.
        </p>

        {status === 'error' && (
          <div className="krv-alert" role="alert">
            {fixFor(error?.code)}
          </div>
        )}

        <div className="krv-fields">
          <label className="krv-field" htmlFor="krv-repo-url">
            <span className="krv-label">Repository URL</span>
            <input
              id="krv-repo-url"
              className="krv-input"
              type="text"
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              placeholder="https://github.com/your-org/engagement-repo"
              disabled={connecting}
            />
          </label>
          <label className="krv-field" htmlFor="krv-repo-token">
            <span className="krv-label">Access token</span>
            <input
              id="krv-repo-token"
              className="krv-input"
              type="password"
              value={token}
              onChange={(event) => setToken(event.target.value)}
              placeholder="A fine-grained token with write access to this repo"
              disabled={connecting}
              autoComplete="off"
            />
          </label>
        </div>

        <p className="krv-vendor-note">
          <ShieldCheck size={16} aria-hidden="true" /> Use a token scoped to just
          this repository. It is stored encrypted on the server and never sent
          back to your browser.
        </p>

        <div className="krv-actions">
          <button
            type="button"
            className="krv-primary"
            onClick={onConnect}
            disabled={connecting}
            aria-busy={connecting}
          >
            <Github size={18} aria-hidden="true" />
            {connecting ? 'Connecting…' : 'Connect'}
          </button>
        </div>
      </main>
    </div>
  );
};

export default KnowledgeRepoView;
